import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Filter } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import CategoryCard from "@/components/marketplace/CategoryCard";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import api from "@/lib/api";

const KINDS = [
  { key: "all", label: "All categories" },
  { key: "local", label: "Local services" },
  { key: "digital", label: "Digital talent" },
];

export default function CategoriesPage() {
  const [tree, setTree] = useState([]);
  const [kind, setKind] = useState("all");
  const [openId, setOpenId] = useState(null);
  useDocumentTitle("Browse categories");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/categories/tree");
      setTree(data);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (kind === "all") return tree;
    return tree.filter((t) => t.kind === kind);
  }, [tree, kind]);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Explore</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-[#071b22] tracking-tight" data-testid="categories-title">
              Every service on the Gulf Coast — in one place.
            </h1>
            <p className="mt-4 text-[#252A34]/75 max-w-2xl leading-relaxed">
              From house cleaning in Pensacola to full-stack development from Baton Rouge. Choose a category to see verified pros.
            </p>
          </div>
          <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1" role="tablist">
            {KINDS.map((k) => (
              <button
                key={k.key}
                onClick={() => setKind(k.key)}
                data-testid={`kind-filter-${k.key}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  kind === k.key ? "bg-white text-[#071b22] shadow-sm" : "text-[#252A34]/70"
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.map((root) => (
          <section key={root.id} className="mb-16">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-display text-xl md:text-2xl font-semibold text-[#071b22]" data-testid={`category-section-${root.slug}`}>
                {root.name}
                <span className="ml-3 align-middle text-[10px] font-bold uppercase tracking-[0.18em] text-[#165a71]">{root.kind === "digital" ? "Digital" : root.children?.some((c) => c.kind !== root.kind) ? "Local + remote" : "Local"}</span>
              </h2>
              <div className="text-xs text-[#252A34]/60">{root.children?.length || 0} categories</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {(root.children && root.children.length ? root.children : [root]).map((c, i) => (
                <div key={c.id} className="relative">
                  <CategoryCard category={c} index={i} />
                  {c.children?.length > 0 && (
                    <button
                      onClick={() => setOpenId(openId === c.id ? null : c.id)}
                      data-testid={`subcategory-toggle-${c.slug}`}
                      className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-xs font-medium text-[#165a71] border border-[#165a71]/20 hover:bg-[#165a71] hover:text-white transition-colors"
                    >
                      {c.children.length} sub
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                  {openId === c.id && c.children?.length > 0 && (
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 space-y-1.5" data-testid={`subcategories-${c.slug}`}>
                      {c.children.map((sc) => (
                        <Link
                          key={sc.id}
                          to={`/providers?category_id=${sc.id}`}
                          className="block text-sm text-[#252A34]/80 hover:text-[#165a71] transition-colors"
                        >
                          → {sc.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </PublicLayout>
  );
}
